import type { GapSignal, Topic, Concept } from "./types";

export type InterpretationResult = {
  classifiedError: ErrorClassification;
  taggedConcepts: TaggedConcept[];
  confidence: number;
  reasoning: string;
  suggestedFollowUp?: string;
};

export type ErrorClassification =
  | "conceptual_misunderstanding"
  | "factual_error"
  | "procedural_mistake"
  | "vocabulary_confusion"
  | "reasoning_gap"
  | "syntax_error"
  | "comprehension_failure"
  | "no_error";

export type TaggedConcept = {
  conceptId: string;
  conceptName: string;
  topicId: string;
  topicName: string;
  relevanceScore: number;
  evidence?: string;
};

export type StudentReasoning = {
  id: string;
  studentId: string;
  content: string;
  context: {
    topicId?: string;
    lessonId?: string;
    questionPrompt?: string;
    correctAnswer?: string;
    studentAnswer?: string;
  };
  interpreted?: InterpretationResult;
  createdAt: string;
};

const ERROR_PATTERNS: Record<ErrorClassification, { keywords: string[]; weight: number }> = {
  conceptual_misunderstanding: {
    keywords: ["think", "believe", "understand", "concept", "idea", "wrong", " misconception"],
    weight: 0.9
  },
  factual_error: {
    keywords: ["fact", "remember", "know", "is", "are", "was", "were"],
    weight: 0.85
  },
  procedural_mistake: {
    keywords: ["step", "method", "process", "how to", "should", "order", "sequence"],
    weight: 0.8
  },
  vocabulary_confusion: {
    keywords: ["word", "term", "meaning", "define", "vocabulary", "use", "which word"],
    weight: 0.75
  },
  reasoning_gap: {
    keywords: ["why", "because", "therefore", "reason", "conclude", "so", "thus", "hence"],
    weight: 0.7
  },
  syntax_error: {
    keywords: ["sentence", "grammar", "punctuation", "spelling", "write", "language"],
    weight: 0.85
  },
  comprehension_failure: {
    keywords: ["read", "passage", "text", "paragraph", "main idea", "summarize", "what does"],
    weight: 0.8
  },
  no_error: {
    keywords: [],
    weight: 0
  }
};

export function classifyErrorFromMessage(message: string, context?: StudentReasoning["context"]): ErrorClassification {
  const lowerMessage = message.toLowerCase();
  
  const scores: Record<ErrorClassification, number> = {
    conceptual_misunderstanding: 0,
    factual_error: 0,
    procedural_mistake: 0,
    vocabulary_confusion: 0,
    reasoning_gap: 0,
    syntax_error: 0,
    comprehension_failure: 0,
    no_error: 0
  };

  for (const [errorType, config] of Object.entries(ERROR_PATTERNS)) {
    for (const keyword of config.keywords) {
      if (lowerMessage.includes(keyword)) {
        scores[errorType as ErrorClassification] += config.weight;
      }
    }
  }

  const questionPatterns = [
    { pattern: /^(what|where|when|who|which)/i, types: ["factual_error", "vocabulary_confusion"] },
    { pattern: /^(how|why|explain)/i, types: ["reasoning_gap", "conceptual_misunderstanding"] },
    { pattern: /(error|mistake|wrong|incorrect|confused)/i, types: ["conceptual_misunderstanding", "procedural_mistake"] },
    { pattern: /(don't understand|confused|lost|baffled)/i, types: ["comprehension_failure", "reasoning_gap"] }
  ];

  for (const { pattern, types } of questionPatterns) {
    if (pattern.test(message)) {
      for (const type of types) {
        scores[type as ErrorClassification] += 0.3;
      }
    }
  }

  if (context?.questionPrompt) {
    const promptLower = context.questionPrompt.toLowerCase();
    if (promptLower.includes("identify") || promptLower.includes("define")) {
      scores.vocabulary_confusion += 0.2;
    }
    if (promptLower.includes("analyze") || promptLower.includes("evaluate")) {
      scores.reasoning_gap += 0.2;
    }
    if (promptLower.includes("write") || promptLower.includes("construct")) {
      scores.syntax_error += 0.2;
    }
  }

  if (context?.correctAnswer && context?.studentAnswer) {
    const isIncorrect = context.studentAnswer.trim().toLowerCase() !== context.correctAnswer.trim().toLowerCase();
    if (!isIncorrect) {
      return "no_error";
    }
  }

  let maxType: ErrorClassification = "no_error";
  let maxScore = 0;
  
  for (const [type, score] of Object.entries(scores)) {
    if (score > maxScore) {
      maxScore = score;
      maxType = type as ErrorClassification;
    }
  }

  if (maxScore < 0.3) {
    return "no_error";
  }

  return maxType;
}

export function tagConceptsFromError(
  errorType: ErrorClassification,
  message: string,
  topics: Topic[],
  concepts: Concept[]
): TaggedConcept[] {
  const tagged: TaggedConcept[] = [];
  const lowerMessage = message.toLowerCase();
  
  for (const concept of concepts) {
    const conceptNameLower = concept.name.toLowerCase();
    const conceptDescLower = concept.description.toLowerCase();
    
    let relevanceScore = 0;
    
    if (lowerMessage.includes(conceptNameLower) || lowerMessage.includes(conceptDescLower.slice(0, 30))) {
      relevanceScore += 0.5;
    }
    
    const topic = topics.find(t => t.id === concept.topicId);
    if (topic) {
      for (const prereqId of (topic.prerequisiteTopicIds || [])) {
        const prereqTopic = topics.find(t => t.id === prereqId);
        if (prereqTopic && (lowerMessage.includes(prereqTopic.name.toLowerCase()))) {
          relevanceScore += 0.3;
        }
      }
    }

    switch (errorType) {
      case "vocabulary_confusion":
        if (conceptNameLower.includes("vocabulary") || conceptNameLower.includes("word") || conceptNameLower.includes("definition")) {
          relevanceScore += 0.4;
        }
        break;
      case "syntax_error":
        if (conceptNameLower.includes("sentence") || conceptNameLower.includes("grammar") || conceptNameLower.includes("clause")) {
          relevanceScore += 0.4;
        }
        break;
      case "comprehension_failure":
        if (conceptNameLower.includes("comprehension") || conceptNameLower.includes("reading") || conceptNameLower.includes("literal")) {
          relevanceScore += 0.4;
        }
        break;
      case "reasoning_gap":
        if (conceptNameLower.includes("inference") || conceptNameLower.includes("reasoning") || conceptNameLower.includes("analysis")) {
          relevanceScore += 0.4;
        }
        break;
      case "conceptual_misunderstanding":
        if (conceptNameLower.includes("understanding") || conceptNameLower.includes("concept") || conceptNameLower.includes("main")) {
          relevanceScore += 0.4;
        }
        break;
      case "procedural_mistake":
        if (conceptNameLower.includes("structure") || conceptNameLower.includes("writing") || conceptNameLower.includes("paragraph")) {
          relevanceScore += 0.4;
        }
        break;
    }

    if (relevanceScore > 0.2) {
      tagged.push({
        conceptId: concept.id,
        conceptName: concept.name,
        topicId: concept.topicId,
        topicName: topic?.name || "Unknown Topic",
        relevanceScore
      });
    }
  }

  return tagged.sort((a, b) => b.relevanceScore - a.relevanceScore).slice(0, 5);
}

export function interpretStudentReasoning(
  message: string,
  context: StudentReasoning["context"],
  topics: Topic[],
  concepts: Concept[]
): InterpretationResult {
  const classifiedError = classifyErrorFromMessage(message, context);
  
  const taggedConcepts = classifiedError !== "no_error" 
    ? tagConceptsFromError(classifiedError, message, topics, concepts)
    : [];

  const confidence = calculateConfidence(classifiedError, taggedConcepts, message);
  
  const reasoning = generateReasoningExplanation(classifiedError, taggedConcepts, message);
  
  const suggestedFollowUp = generateFollowUpQuestion(classifiedError, taggedConcepts, message);

  return {
    classifiedError,
    taggedConcepts,
    confidence,
    reasoning,
    suggestedFollowUp
  };
}

function calculateConfidence(
  errorType: ErrorClassification,
  taggedConcepts: TaggedConcept[],
  message: string
): number {
  if (errorType === "no_error") {
    return 0.95;
  }

  let confidence = 0.5;

  const patternMatchScore = ERROR_PATTERNS[errorType]?.weight || 0.5;
  confidence += patternMatchScore * 0.3;

  if (taggedConcepts.length > 0) {
    const avgRelevance = taggedConcepts.reduce((sum, c) => sum + c.relevanceScore, 0) / taggedConcepts.length;
    confidence += avgRelevance * 0.3;
  }

  const messageLength = message.split(" ").length;
  if (messageLength > 10) {
    confidence += 0.1;
  } else if (messageLength < 3) {
    confidence -= 0.2;
  }

  return Math.min(0.95, Math.max(0.3, confidence));
}

function generateReasoningExplanation(
  errorType: ErrorClassification,
  taggedConcepts: TaggedConcept[],
  message: string
): string {
  if (errorType === "no_error") {
    return "The student's response appears to be correct and demonstrates understanding of the topic.";
  }

  const conceptNames = taggedConcepts.map(c => c.conceptName).slice(0, 3);
  
  switch (errorType) {
    case "conceptual_misunderstanding":
      return `The student appears to have a conceptual misunderstanding${conceptNames.length > 0 ? ` related to: ${conceptNames.join(", ")}` : ""}. The response suggests confusion about fundamental concepts.`;
    case "factual_error":
      return `The student's response contains a factual error${conceptNames.length > 0 ? ` involving: ${conceptNames.join(", ")}` : ""}. This may indicate a need to review basic facts.`;
    case "procedural_mistake":
      return `The student made a procedural mistake${conceptNames.length > 0 ? ` in: ${conceptNames.join(", ")}` : ""}. The steps or method used appear to be incorrect.`;
    case "vocabulary_confusion":
      return `The student shows vocabulary confusion${conceptNames.length > 0 ? ` with terms related to: ${conceptNames.join(", ")}` : ""}. Understanding of key terminology may be unclear.`;
    case "reasoning_gap":
      return `The student has a reasoning gap${conceptNames.length > 0 ? ` in: ${conceptNames.join(", ")}` : ""}. The logical connection between ideas is not fully developed.`;
    case "syntax_error":
      return `The student made a syntax error${conceptNames.length > 0 ? ` in: ${conceptNames.join(", ")}` : ""}. Grammar or sentence structure needs attention.`;
    case "comprehension_failure":
      return `The student shows comprehension failure${conceptNames.length > 0 ? ` for: ${conceptNames.join(", ")}` : ""}. The text or passage meaning was not fully grasped.`;
    default:
      return "The student's response indicates an area that needs further investigation.";
  }
}

function generateFollowUpQuestion(
  errorType: ErrorClassification,
  taggedConcepts: TaggedConcept[],
  message: string
): string | undefined {
  if (errorType === "no_error") return undefined;

  const topConcept = taggedConcepts[0];
  
  switch (errorType) {
    case "conceptual_misunderstanding":
      return topConcept 
        ? `Can you explain what you understand about "${topConcept.conceptName}" in your own words?`
        : "Can you explain your thinking about this topic?";
    case "factual_error":
      return "What information do you recall about this topic from your studies?";
    case "procedural_mistake":
      return topConcept
        ? `What steps would you follow to ${topConcept.topicName.toLowerCase()}?`
        : "Walk me through the steps you took to solve this.";
    case "vocabulary_confusion":
      return topConcept
        ? `What does the term "${topConcept.conceptName}" mean to you?`
        : "What do you think this word or term means?";
    case "reasoning_gap":
      return "Why do you think that's the answer? What evidence supports your thinking?";
    case "syntax_error":
      return "Read your response aloud. Do you notice anything that could be improved?";
    case "comprehension_failure":
      return "What was the main point of the passage you just read?";
    default:
      return undefined;
  }
}

export function buildGapSignalsFromInterpretation(
  interpretation: InterpretationResult,
  studentId: string
): GapSignal[] {
  if (interpretation.classifiedError === "no_error") {
    return [];
  }

  return interpretation.taggedConcepts.map((concept, index) => ({
    id: `interpreted-gap-${studentId}-${concept.conceptId}-${Date.now()}`,
    topicId: concept.topicId,
    topicName: concept.topicName,
    conceptId: concept.conceptId,
    conceptName: concept.conceptName,
    relatedTopicId: concept.topicId,
    signalSource: "chat" as const,
    severity: interpretation.confidence > 0.7 ? "high" : interpretation.confidence > 0.5 ? "medium" : "low",
    confidence: interpretation.confidence * concept.relevanceScore,
    evidence: `${interpretation.classifiedError.replace(/_/g, " ")} detected: ${interpretation.reasoning}`,
    recommendedAction: interpretation.suggestedFollowUp 
      ? `Ask follow-up: "${interpretation.suggestedFollowUp}"`
      : `Review concept: ${concept.conceptName}`
  }));
}

export function createSocraticPrompt(
  interpretation: InterpretationResult,
  topicName: string,
  studentMessage: string
): string {
  const topConcept = interpretation.taggedConcepts[0];
  
  let prompt = `The student is working on "${topicName}" and said: "${studentMessage}"\n\n`;
  
  prompt += `Analysis: ${interpretation.reasoning}\n\n`;
  
  prompt += `The error type is: ${interpretation.classifiedError.replace(/_/g, " ")}\n`;
  
  if (topConcept) {
    prompt += `Most relevant concept: ${topConcept.conceptName}\n`;
  }
  
  prompt += `\nGenerate a Socratic follow-up question that:\n`;
  prompt += `1. Helps the student discover their misconception\n`;
  prompt += `2. Is concise (1-2 sentences)\n`;
  prompt += `3. References the topic or concept being discussed\n`;
  prompt += `4. Encourages deeper thinking without giving away the answer\n`;
  
  if (interpretation.suggestedFollowUp) {
    prompt += `\nSuggested starting point: "${interpretation.suggestedFollowUp}"`;
  }
  
  return prompt;
}

export function shouldTriggerIntervention(
  interpretation: InterpretationResult,
  recentInterpretations: InterpretationResult[]
): { shouldTrigger: boolean; interventionType: "socratic" | "review" | "quiz" | "none"; reason: string } {
  if (interpretation.classifiedError === "no_error") {
    return { shouldTrigger: false, interventionType: "none", reason: "No error detected" };
  }

  if (interpretation.confidence < 0.5) {
    return { 
      shouldTrigger: false, 
      interventionType: "socratic", 
      reason: "Low confidence - need more information via dialogue" 
    };
  }

  const similarErrors = recentInterpretations.filter(
    r => r.classifiedError === interpretation.classifiedError
  ).length;

  if (similarErrors >= 2) {
    return {
      shouldTrigger: true,
      interventionType: "review",
      reason: `Repeated ${interpretation.classifiedError.replace(/_/g, " ")} detected (${similarErrors + 1} times) - student needs structured review`
    };
  }

  if (interpretation.taggedConcepts.length >= 2 && interpretation.confidence > 0.7) {
    return {
      shouldTrigger: true,
      interventionType: "quiz",
      reason: "Multiple related concepts flagged with high confidence - validate with micro-assessment"
    };
  }

  if (interpretation.classifiedError === "reasoning_gap" || interpretation.classifiedError === "conceptual_misunderstanding") {
    return {
      shouldTrigger: true,
      interventionType: "socratic",
      reason: "Higher-order error detected - engage with dialogue to expose misconception"
    };
  }

  return {
    shouldTrigger: false,
    interventionType: "socratic",
    reason: "Single low-severity error - continue with gentle Socratic follow-up"
  };
}
